import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { INK, axisProps, gridProps } from "../../Reports/chartTheme";
import { trendRows } from "./reportModel";

/* --------------------------------------------------------------------------
   Visual building blocks for the portfolio report: animated numbers, rings,
   sparklines, and the recharts views. Chart colours come from chartTheme so
   the report matches the per-project reports page.
   -------------------------------------------------------------------------- */

const prefersReducedMotion = () => {
  try {
    return Boolean(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  } catch (error) {
    return false;
  }
};

const EASE_DURATION = 900;

/** Counts from the previous value to `target` with an ease-out curve. */
export const useCountUp = (target = 0, duration = EASE_DURATION) => {
  const to = Number(target) || 0;
  const [value, setValue] = useState(to);
  const fromRef = useRef(0);

  useEffect(() => {
    if (prefersReducedMotion() || typeof window.requestAnimationFrame !== "function") {
      setValue(to);
      fromRef.current = to;
      return undefined;
    }
    const from = fromRef.current;
    const startedAt = performance.now();
    let frame;
    const tick = (now) => {
      const t = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(from + (to - from) * eased);
      if (t < 1) frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frame);
      fromRef.current = to;
    };
  }, [to, duration]);

  return value;
};

export const CountUp = ({ value = 0, suffix = "", decimals = 0 }) => {
  const current = useCountUp(value);
  return (
    <>
      {Number(current).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
      {suffix}
    </>
  );
};

/* ---------------------------------------------------------------- people */

const hueOf = (text = "") => {
  let hue = 0;
  for (let i = 0; i < text.length; i += 1) hue = (hue * 31 + text.charCodeAt(i)) % 360;
  return hue;
};

export const initialsOf = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return `${parts[0][0] || ""}${parts.length > 1 ? parts[parts.length - 1][0] : ""}`.toUpperCase();
};

export const Avatar = ({ name = "", size = 28 }) => {
  const hue = hueOf(name);
  return (
    <span
      className="pr-avatar"
      title={name}
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.4),
        background: `linear-gradient(135deg, hsl(${hue} 68% 56%), hsl(${(hue + 38) % 360} 70% 40%))`,
      }}
    >
      {initialsOf(name)}
    </span>
  );
};

export const AvatarStack = ({ people = [], max = 4, size = 28 }) => {
  if (!people.length) return <span className="pr-muted">No members</span>;
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;
  return (
    <span className="pr-avatars" aria-label={`${people.length} ${people.length === 1 ? "member" : "members"}`}>
      {shown.map((person) => (
        <Avatar key={person.id || person.name} name={person.name} size={size} />
      ))}
      {extra > 0 ? (
        <span className="pr-avatar pr-avatar--more" style={{ width: size, height: size }}>
          +{extra}
        </span>
      ) : null}
    </span>
  );
};

export const ProjectMark = ({ name = "", color, size = "md" }) => (
  <span className={`pr-mark pr-mark--${size}`} style={{ "--c": color }} aria-hidden="true">
    {(name || "?").charAt(0).toUpperCase()}
  </span>
);

/* --------------------------------------------------------- small visuals */

export const ProgressRing = ({ value = 0, size = 88, stroke = 9, color = "#00ac56", sublabel }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const [shown, setShown] = useState(0);

  useEffect(() => {
    // one frame at zero first, so the stroke animates in rather than appearing
    const timer = setTimeout(() => setShown(Math.max(0, Math.min(100, value))), 40);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="pr-ring" style={{ width: size, height: size }} role="img" aria-label={`${value}% complete`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#eef1f6" strokeWidth={stroke} />
        <circle
          className="pr-ring__value"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeOpacity={shown > 0 ? 1 : 0}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - shown / 100)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className="pr-ring__label">
        <strong>
          <CountUp value={value} suffix="%" />
        </strong>
        {sublabel ? <span>{sublabel}</span> : null}
      </span>
    </div>
  );
};

export const SparkBars = ({ history = [], color = "#00ac56", height = 36 }) => {
  if (!history.length) return <span className="pr-muted pr-spark-empty">No sprints yet</span>;
  const max = Math.max(1, ...history.map((sprint) => sprint.total));
  return (
    <span
      className="pr-spark"
      style={{ height, "--c": color }}
      role="img"
      aria-label={`Last ${history.length} sprints: ${history.map((sprint) => `${sprint.name} ${sprint.done} of ${sprint.total}`).join(", ")}`}
    >
      {history.map((sprint, index) => (
        <span
          key={sprint.id}
          className={`pr-spark__col ${sprint.active ? "is-active" : ""}`}
          title={`${sprint.name}: ${sprint.done}/${sprint.total} done`}
          style={{ "--i": index }}
        >
          <span className="pr-spark__total" style={{ height: `${Math.max(8, (sprint.total / max) * 100)}%` }}>
            <span className="pr-spark__done" style={{ height: sprint.total ? `${(sprint.done / sprint.total) * 100}%` : "0%" }} />
          </span>
        </span>
      ))}
    </span>
  );
};

export const StackBar = ({ segments = [], height = 8 }) => {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  if (!total) return <span className="pr-stack is-empty" style={{ height }} />;
  return (
    <span className="pr-stack" style={{ height }} role="img" aria-label={segments.map((segment) => `${segment.label} ${segment.value}`).join(", ")}>
      {segments.map((segment, index) => (
        <span
          key={segment.label}
          title={`${segment.label}: ${segment.value}`}
          style={{ width: `${(segment.value / total) * 100}%`, background: segment.color, "--i": index }}
        />
      ))}
    </span>
  );
};

export const BarList = ({ segments = [], emptyText = "Nothing to show" }) => {
  const max = Math.max(1, ...segments.map((segment) => segment.value));
  if (!segments.length) return <p className="pr-muted">{emptyText}</p>;
  return (
    <ul className="pr-barlist">
      {segments.map((segment, index) => (
        <li key={segment.label} style={{ "--i": index }}>
          <span className="pr-barlist__label">
            <i style={{ background: segment.color }} />
            {segment.label}
          </span>
          <span className="pr-barlist__track">
            <span style={{ width: `${(segment.value / max) * 100}%`, background: segment.color }} />
          </span>
          <strong>{segment.value}</strong>
        </li>
      ))}
    </ul>
  );
};

/* ---------------------------------------------------------------- charts */

const TooltipCard = ({ title, rows }) => (
  <div className="pr-tooltip">
    {title ? <div className="pr-tooltip__title">{title}</div> : null}
    {rows.map((row) => (
      <div className="pr-tooltip__row" key={row.label}>
        <i style={{ background: row.color }} />
        <span>{row.label}</span>
        <strong>{row.value}</strong>
      </div>
    ))}
  </div>
);

const CHART_ANIMATION = { animationDuration: 900, animationEasing: "ease-out" };

/** Current (or last) sprint of every project as a rounded done/remaining bar. */
export const SprintProgressChart = ({ views = [] }) => {
  const data = views.map((view) => ({
    name: view.name,
    done: view.focus?.done || 0,
    remaining: view.focus?.remaining || 0,
    color: view.color,
    sprint: view.focus?.name || "No sprint",
    percent: view.completion,
  }));
  const height = Math.max(180, data.length * 46 + 36);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 18, left: 0, bottom: 0 }} barCategoryGap={14}>
        <defs>
          {data.map((entry, index) => (
            <linearGradient key={entry.name} id={`pr-progress-${index}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor={entry.color} stopOpacity={0.7} />
              <stop offset="1" stopColor={entry.color} stopOpacity={1} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid {...gridProps} horizontal={false} vertical />
        <XAxis type="number" allowDecimals={false} {...axisProps} />
        <YAxis type="category" dataKey="name" width={118} {...axisProps} tick={{ ...axisProps.tick, fill: INK.secondary, fontWeight: 600 }} />
        <Tooltip
          cursor={{ fill: "rgba(14, 95, 193, 0.04)" }}
          content={({ active, payload }) =>
            active && payload?.length ? (
              <TooltipCard
                title={`${payload[0].payload.name} · ${payload[0].payload.sprint}`}
                rows={[
                  { label: "Done", value: payload[0].payload.done, color: payload[0].payload.color },
                  { label: "Remaining", value: payload[0].payload.remaining, color: "#d7dde7" },
                  { label: "Complete", value: `${payload[0].payload.percent}%`, color: "transparent" },
                ]}
              />
            ) : null
          }
        />
        <Bar dataKey="done" name="Done" stackId="progress" radius={[8, 0, 0, 8]} {...CHART_ANIMATION}>
          {data.map((entry, index) => (
            <Cell key={entry.name} fill={`url(#pr-progress-${index})`} />
          ))}
        </Bar>
        <Bar dataKey="remaining" name="Remaining" stackId="progress" fill="#e9edf3" radius={[0, 8, 8, 0]} {...CHART_ANIMATION} />
      </BarChart>
    </ResponsiveContainer>
  );
};

/** Completion % of each project's recent sprints, aligned on the latest sprint. */
export const DeliveryTrendChart = ({ views = [] }) => {
  const [hidden, setHidden] = useState(() => new Set());
  const rows = useMemo(() => trendRows(views), [views]);

  const toggle = (id) =>
    setHidden((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (!rows.length) return <p className="pr-muted pr-chart-empty">No sprint history yet.</p>;

  return (
    <div className="pr-trend">
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={rows} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
          <defs>
            {views.map((view) => (
              <linearGradient key={view.id} id={`pr-trend-${view.id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor={view.color} stopOpacity={0.22} />
                <stop offset="1" stopColor={view.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="slot" {...axisProps} />
          <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={(tick) => `${tick}%`} {...axisProps} />
          <Tooltip
            cursor={{ stroke: "#d7dde7", strokeDasharray: "4 4" }}
            content={({ active, payload, label }) =>
              active && payload?.length ? (
                <TooltipCard
                  title={label}
                  rows={payload
                    .filter((entry) => entry.value !== null && entry.value !== undefined)
                    .map((entry) => ({
                      label: `${entry.name}${entry.payload[`${entry.dataKey}__name`] ? ` · ${entry.payload[`${entry.dataKey}__name`]}` : ""}`,
                      value: `${entry.value}%`,
                      color: entry.stroke,
                    }))}
                />
              ) : null
            }
          />
          {views.map((view) =>
            hidden.has(view.id) ? null : (
              <Area
                key={view.id}
                type="monotone"
                dataKey={view.id}
                name={view.name}
                stroke={view.color}
                strokeWidth={2.5}
                fill={`url(#pr-trend-${view.id})`}
                dot={{ r: 3, strokeWidth: 2, fill: "#ffffff" }}
                activeDot={{ r: 5, strokeWidth: 0 }}
                connectNulls
                {...CHART_ANIMATION}
              />
            )
          )}
        </AreaChart>
      </ResponsiveContainer>
      <div className="pr-legend" role="group" aria-label="Show or hide projects">
        {views.map((view) => (
          <button
            key={view.id}
            type="button"
            className={`pr-legend__item ${hidden.has(view.id) ? "is-off" : ""}`}
            onClick={() => toggle(view.id)}
            aria-pressed={!hidden.has(view.id)}
          >
            <i style={{ background: view.color }} />
            {view.name}
          </button>
        ))}
      </div>
    </div>
  );
};

/** Donut with a centred total and a legend that highlights on hover. */
export const MixDonut = ({ segments = [], totalLabel = "issues", size = 200 }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  if (!total) return <p className="pr-muted pr-chart-empty">No issues to break down yet.</p>;

  return (
    <div className="pr-donut">
      <div className="pr-donut__chart" style={{ width: size, height: size }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={segments}
              dataKey="value"
              nameKey="label"
              innerRadius="68%"
              outerRadius="96%"
              paddingAngle={segments.length > 1 ? 2 : 0}
              cornerRadius={6}
              stroke="none"
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
              {...CHART_ANIMATION}
            >
              {segments.map((segment, index) => (
                <Cell key={segment.label} fill={segment.color} opacity={activeIndex === null || activeIndex === index ? 1 : 0.35} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <span className="pr-donut__center">
          <strong>
            <CountUp value={activeIndex === null ? total : segments[activeIndex].value} />
          </strong>
          <span>{activeIndex === null ? totalLabel : segments[activeIndex].label}</span>
        </span>
      </div>
      <ul className="pr-donut__legend">
        {segments.map((segment, index) => (
          <li
            key={segment.label}
            className={activeIndex === index ? "is-active" : ""}
            onMouseEnter={() => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
            style={{ "--i": index }}
          >
            <i style={{ background: segment.color }} />
            <span className="pr-donut__name">{segment.label}</span>
            <strong>{segment.value}</strong>
            <span className="pr-donut__pct">{Math.round((segment.value / total) * 100)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** One project's sprints as done/remaining columns, the active one emphasised. */
export const SprintHistoryChart = ({ history = [], color = "#00ac56" }) => {
  if (!history.length) return <p className="pr-muted pr-chart-empty">No sprints yet.</p>;
  const data = history.map((sprint) => ({ ...sprint, label: sprint.name }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }} barCategoryGap="28%">
        <CartesianGrid {...gridProps} />
        <XAxis dataKey="label" {...axisProps} interval={0} tick={{ ...axisProps.tick, fontSize: 11 }} />
        <YAxis allowDecimals={false} {...axisProps} />
        <Tooltip
          cursor={{ fill: "rgba(14, 95, 193, 0.04)" }}
          content={({ active, payload }) =>
            active && payload?.length ? (
              <TooltipCard
                title={`${payload[0].payload.name}${payload[0].payload.active ? " · active" : ""}`}
                rows={[
                  { label: "Done", value: payload[0].payload.done, color },
                  { label: "Remaining", value: payload[0].payload.remaining, color: "#d7dde7" },
                  { label: "Complete", value: `${payload[0].payload.percent}%`, color: "transparent" },
                ]}
              />
            ) : null
          }
        />
        <Bar dataKey="done" name="Done" stackId="sprint" {...CHART_ANIMATION}>
          {data.map((entry) => (
            <Cell key={entry.id} fill={color} fillOpacity={entry.active ? 1 : 0.7} />
          ))}
        </Bar>
        <Bar dataKey="remaining" name="Remaining" stackId="sprint" fill="#e3e8ef" radius={[6, 6, 0, 0]} {...CHART_ANIMATION} />
      </BarChart>
    </ResponsiveContainer>
  );
};

/** Open and done issues per person in the active sprint. */
export const WorkloadChart = ({ workload = [] }) => {
  if (!workload.length) return <p className="pr-muted pr-chart-empty">No one has work in the active sprint.</p>;
  const height = Math.max(140, workload.length * 38 + 20);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={workload} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }} barCategoryGap={10}>
        <CartesianGrid {...gridProps} horizontal={false} vertical />
        <XAxis type="number" allowDecimals={false} {...axisProps} />
        <YAxis type="category" dataKey="name" width={112} {...axisProps} tick={{ ...axisProps.tick, fill: INK.secondary }} />
        <Tooltip
          cursor={{ fill: "rgba(14, 95, 193, 0.04)" }}
          content={({ active, payload }) =>
            active && payload?.length ? (
              <TooltipCard
                title={payload[0].payload.name}
                rows={[
                  { label: "Open", value: payload[0].payload.open, color: "#2a78d6" },
                  { label: "Done", value: payload[0].payload.done, color: "#1baf7a" },
                  { label: "Points", value: payload[0].payload.points, color: "transparent" },
                ]}
              />
            ) : null
          }
        />
        <Bar dataKey="done" name="Done" stackId="load" fill="#1baf7a" radius={[6, 0, 0, 6]} {...CHART_ANIMATION} />
        <Bar dataKey="open" name="Open" stackId="load" fill="#2a78d6" radius={[0, 6, 6, 0]} {...CHART_ANIMATION} />
      </BarChart>
    </ResponsiveContainer>
  );
};
